import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { SurveyorProfile, SurveyorMetrics } from '../../types';
import { getSurveyorRank } from '../../utils/surveyorRank';
import { toNumber } from '../../utils/number';

export const AdminSurveyorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [surveyor, setSurveyor] = useState<SurveyorProfile | null>(null);
  const [metrics, setMetrics] = useState<SurveyorMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [penaltyPoints, setPenaltyPoints] = useState('10');
  const [penaltyReason, setPenaltyReason] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [showPenaltyForm, setShowPenaltyForm] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const sv = await adminService.getSurveyor(id);
        setSurveyor(sv);
        const met = await adminService.getSurveyorMetrics(id).then(r => r[0] || null).catch(() => null);
        setMetrics(met);
      } catch (error: any) {
        setSurveyor(null);
        setMetrics(null);
        setLoadError(error?.response?.data?.detail || error?.message || 'Failed to load surveyor profile.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  const doAction = async (action: 'approve' | 'reject') => {
    if (!id) return;
    if (action === 'reject' && !declineReason.trim()) {
      setActionMsg('❌ Please write a reason for declining this surveyor.');
      return;
    }
    try {
      const result = action === 'approve'
        ? await adminService.approveSurveyor(id, approvalComments.trim())
        : await adminService.rejectSurveyor(id, declineReason.trim());
      setSurveyor(result);
      setApprovalComments('');
      setDeclineReason('');
      setActionMsg(`✅ Surveyor ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);
    } catch (e: any) {
      setActionMsg(`❌ ${e?.message || 'Action failed'}`);
    }
  };

  const doSuspend = async () => {
    if (!id || !suspendReason.trim()) return;
    try {
      const result = await adminService.suspendSurveyor(id, { reason: suspendReason.trim() });
      setSurveyor(result);
      setShowSuspendForm(false);
      setSuspendReason('');
      setActionMsg('✅ Surveyor suspended. They cannot access new jobs.');
    } catch (e: any) { setActionMsg(`❌ ${e?.message || 'Suspend failed'}`); }
  };

  const doUnsuspend = async () => {
    if (!id) return;
    try {
      const result = await adminService.unsuspendSurveyor(id);
      setSurveyor(result);
      setActionMsg('✅ Surveyor reinstated successfully.');
    } catch (e: any) { setActionMsg(`❌ ${e?.message || 'Unsuspend failed'}`); }
  };

  const doAddPenalty = async () => {
    if (!id || !penaltyReason.trim()) return;
    try {
      const result = await adminService.addPenalty(id, { points: parseInt(penaltyPoints) || 10, reason: penaltyReason.trim() });
      setSurveyor(result);
      setShowPenaltyForm(false);
      setPenaltyReason('');
      setActionMsg(`✅ ${penaltyPoints} penalty points applied. Reputation score updated.`);
    } catch (e: any) { setActionMsg(`❌ ${e?.message || 'Penalty failed'}`); }
  };

  if (isLoading) return <div style={s.center}><div style={s.spinner} /></div>;
  if (!surveyor) {
    return (
      <div style={s.center}>
        <p style={s.errorText}>{loadError || 'Surveyor profile not found.'}</p>
        <button style={s.retryBtn} onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const verColors: Record<string, { bg: string; color: string }> = {
    APPROVED: { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)' },
    PENDING: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)' },
    REJECTED: { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)' },
  };
  const vc = verColors[surveyor.verification_status] || verColors.PENDING;
  const ratingAverage = toNumber(surveyor.rating_average);
  const totalJobs = toNumber(surveyor.total_jobs);
  const rank = getSurveyorRank(ratingAverage, totalJobs);
  const isSuspended = surveyor.is_suspended;

  return (
    <div>
      <button style={s.back} onClick={() => navigate(-1)}>← Back to Surveyors</button>
      <h1 style={s.title}>{(surveyor.first_name || surveyor.last_name) ? `${surveyor.first_name} ${surveyor.last_name}` : surveyor.email || 'Unknown User'}</h1>

      {/* Suspension banner */}
      {isSuspended && (
        <div style={{ background: 'var(--sc-error-bg)', border: '1.5px solid var(--sc-error)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, color: 'var(--sc-error-text)', fontSize: 14 }}>🔒 THIS SURVEYOR IS SUSPENDED</div>
            {surveyor.suspension_reason && <div style={{ fontSize: 13, color: 'var(--sc-error-text)', marginTop: 4 }}>Reason: {surveyor.suspension_reason}</div>}
            {surveyor.suspended_at && <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 2 }}>Since {new Date(surveyor.suspended_at).toLocaleDateString()}</div>}
          </div>
          <button onClick={doUnsuspend} style={{ background: 'var(--sc-success)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>↩ Reinstate</button>
        </div>
      )}

      {actionMsg && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: actionMsg.startsWith('✅') ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)', color: actionMsg.startsWith('✅') ? 'var(--sc-success-text)' : 'var(--sc-error-text)', marginBottom: 16, fontSize: 13 }}>
          {actionMsg}
        </div>
      )}

      <div style={s.grid}>
        {/* Profile */}
        <div>
          <div style={s.card}>
            <div style={s.profileBar}>
              <div style={{ position: 'relative' as const }}>
                <div style={s.avatar}>{(surveyor.first_name?.[0] || surveyor.email?.[0] || '?').toUpperCase()}</div>
                {isSuspended && <div style={{ position: 'absolute' as const, bottom: -4, right: -4, width: 20, height: 20, background: 'var(--sc-error)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, border: '2px solid var(--sc-bg-secondary)' }} title="Suspended">🔒</div>}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--sc-text-primary)' }}>{(surveyor.first_name || surveyor.last_name) ? `${surveyor.first_name} ${surveyor.last_name}` : surveyor.email || 'Unknown User'}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' as const }}>
                  <span style={{ ...s.badge, background: vc.bg, color: vc.color }}>{surveyor.verification_status}</span>
                  <span style={{ ...s.badge, background: rank.bg, color: rank.color }}>{rank.icon} {rank.label}</span>
                  {isSuspended && <span style={{ ...s.badge, background: 'var(--sc-error-bg)', color: 'var(--sc-error-text)' }}>🔒 Suspended</span>}
                </div>
              </div>
            </div>
            {/* Reputation bar */}
            <div style={{ background: 'var(--sc-bg-primary)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--sc-text-secondary)' }}>REPUTATION SCORE</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: (surveyor.reputation_score ?? 100) < 40 ? 'var(--sc-error)' : 'var(--sc-success)' }}>{surveyor.reputation_score ?? 100}/100</span>
              </div>
              <div style={{ height: 6, background: 'var(--sc-border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${surveyor.reputation_score ?? 100}%`, background: (surveyor.reputation_score ?? 100) < 40 ? 'var(--sc-error)' : (surveyor.reputation_score ?? 100) < 70 ? 'var(--sc-warning)' : 'var(--sc-success)', borderRadius: 3, transition: 'width 0.4s' }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 4 }}>Penalty points accumulated: {surveyor.penalty_points ?? 0}</div>
            </div>
            <div style={s.divider} />
            <div style={s.fieldGrid}>
              {[
                ['Phone', surveyor.phone], ['Email', surveyor.email || '—'],
                ['City', surveyor.base_city], ['Area', surveyor.base_area || '—'],
                ['Available', surveyor.is_available ? 'Yes' : 'No'], ['Total Jobs', String(totalJobs)],
              ].map(([l, v]) => (
                <div key={l} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const }}>{l}</div>
                  <div style={{ fontSize: 14, color: 'var(--sc-text-primary)' }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={s.divider} />
            <div style={{ fontWeight: 700, marginBottom: 10, color: 'var(--sc-text-primary)' }}>Service Types</div>
            <div style={s.chipRow}>
              {(surveyor.service_types ?? []).map(st => <span key={st} style={s.chip}>{st}</span>)}
            </div>
          </div>

          {/* Verification docs */}
          <div style={{ ...s.card, marginTop: 14 }}>
            <h3 style={s.cardTitle}>Verification Docs ({(surveyor.verification_docs ?? []).length})</h3>
            {(surveyor.verification_docs ?? []).length === 0 ? (
              <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13 }}>No documents uploaded.</p>
            ) : (
              (surveyor.verification_docs ?? []).map((doc, i) => (
                <a key={i} href={doc} target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--sc-brand-primary)', marginBottom: 6, fontSize: 13 }}>
                  📄 {['National ID / Passport', 'Selfie with ID', 'Tertiary Certificate', 'Resume / CV'][i] ?? `Document ${i + 1}`}
                </a>
              ))
            )}
          </div>
        </div>

        {/* Metrics & Actions */}
        <div>
          {metrics && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>Performance Metrics</h3>
              <div style={s.statGrid}>
                {[
                  ['Assigned', metrics.jobs_assigned],
                  ['Confirmed', metrics.deals_confirmed],
                  ['Closed', metrics.deals_closed],
                  ['Avg Rating', toNumber(metrics.rating_average).toFixed(1)],
                  ['Dispute Rate', `${(toNumber(metrics.dispute_rate) * 100).toFixed(1)}%`],
                ].map(([l, v]) => (
                  <div key={l} style={s.stat}>
                    <div style={s.statVal}>{v}</div>
                    <div style={s.statLabel}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ ...s.card, marginTop: 14 }}>
            <h3 style={s.cardTitle}>Admin Actions</h3>

            {/* Verification */}
            {surveyor.verification_status === 'PENDING' && (
              <div>
                <label style={s.formLabel}>Approval comments (optional)</label>
                <textarea
                  value={approvalComments}
                  onChange={e => setApprovalComments(e.target.value)}
                  placeholder="Optional approval notes or onboarding comments"
                  style={s.textarea}
                />
                <label style={s.formLabel}>Decline reason *</label>
                <textarea
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="Explain what failed review and what must be corrected"
                  style={s.textarea}
                />
                <div style={s.btnGroup}>
                  <button style={s.approveBtn} onClick={() => doAction('approve')}>✅ Approve & Email Terms</button>
                  <button style={s.rejectBtn} onClick={() => doAction('reject')}>❌ Decline & Email Reason</button>
                </div>
              </div>
            )}
            {surveyor.verification_status === 'APPROVED' && (
              <>
                <label style={s.formLabel}>Revocation reason *</label>
                <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Reason for revoking approval" style={s.textarea} />
                <button style={{ ...s.rejectBtn, marginBottom: 10 }} onClick={() => doAction('reject')}>🚫 Revoke Approval</button>
              </>
            )}
            {surveyor.verification_status === 'REJECTED' && (
              <>
                <label style={s.formLabel}>Approval comments (optional)</label>
                <textarea value={approvalComments} onChange={e => setApprovalComments(e.target.value)} placeholder="Optional approval notes" style={s.textarea} />
                <button style={{ ...s.approveBtn, marginBottom: 10 }} onClick={() => doAction('approve')}>✅ Re-Approve & Email Terms</button>
              </>
            )}

            <div style={s.divider} />

            {/* Suspend / Unsuspend */}
            {!isSuspended ? (
              <>
                <button onClick={() => { setShowSuspendForm(v => !v); setShowPenaltyForm(false); }}
                  style={{ width: '100%', padding: 11, background: 'var(--sc-error-bg)', border: '1px solid var(--sc-error)', borderRadius: 9, color: 'var(--sc-error-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
                  🔒 Suspend Surveyor
                </button>
                {showSuspendForm && (
                  <div style={{ background: 'var(--sc-error-bg)', borderRadius: 9, padding: 14, marginBottom: 10, border: '1px solid var(--sc-error)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sc-error-text)', marginBottom: 8 }}>Suspension Reason *</div>
                    <textarea
                      value={suspendReason}
                      onChange={e => setSuspendReason(e.target.value)}
                      placeholder="e.g. Failed to pay commission after 3 reminders"
                      style={{ width: '100%', border: '1px solid var(--sc-border)', borderRadius: 7, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' as const, minHeight: 64, boxSizing: 'border-box' as const, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' }}
                    />
                    <button onClick={doSuspend} disabled={!suspendReason.trim()}
                      style={{ marginTop: 8, padding: '8px 16px', background: 'var(--sc-error)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: suspendReason.trim() ? 1 : 0.5 }}>
                      Confirm Suspension
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button onClick={doUnsuspend}
                style={{ width: '100%', padding: 11, background: 'var(--sc-success-bg)', border: '1px solid var(--sc-success)', borderRadius: 9, color: 'var(--sc-success-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
                ↩ Reinstate (Lift Suspension)
              </button>
            )}

            {/* Reputation Penalty */}
            <button onClick={() => { setShowPenaltyForm(v => !v); setShowSuspendForm(false); }}
              style={{ width: '100%', padding: 11, background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 9, color: 'var(--sc-warning-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              ⚠️ Issue Reputation Penalty
            </button>
            {showPenaltyForm && (
              <div style={{ background: 'var(--sc-warning-bg)', borderRadius: 9, padding: 14, marginTop: 8, border: '1px solid var(--sc-warning)' }}>
                <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sc-warning-text)', marginBottom: 4 }}>POINTS</div>
                    <input type="number" value={penaltyPoints} onChange={e => setPenaltyPoints(e.target.value)} min="1" max="50"
                      style={{ width: '100%', border: '1px solid var(--sc-border)', borderRadius: 7, padding: '7px 10px', fontSize: 14, fontWeight: 700, boxSizing: 'border-box' as const, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' }} />
                  </div>
                  <div style={{ flex: 2 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sc-warning-text)', marginBottom: 4 }}>REASON *</div>
                    <input value={penaltyReason} onChange={e => setPenaltyReason(e.target.value)} placeholder="e.g. Contact info shared in chat"
                      style={{ width: '100%', border: '1px solid var(--sc-border)', borderRadius: 7, padding: '7px 10px', fontSize: 13, boxSizing: 'border-box' as const, background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' }} />
                  </div>
                </div>
                <button onClick={doAddPenalty} disabled={!penaltyReason.trim()}
                  style={{ padding: '8px 16px', background: 'var(--sc-warning)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: penaltyReason.trim() ? 1 : 0.5 }}>
                  Apply Penalty
                </button>
              </div>
            )}

            <div style={{ ...s.divider, marginTop: 10 }} />
            <Link to="/admin/anti-cheat" style={{ fontSize: 12, color: 'var(--sc-text-secondary)', textDecoration: 'none', display: 'block', textAlign: 'center' as const }}>
              🛡️ View Anti-Cheat Monitor
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  errorText: { color: 'var(--sc-error-text)', fontSize: 14, fontWeight: 600, margin: '0 0 12px', textAlign: 'center' as const },
  retryBtn: { padding: '10px 18px', background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  back: { background: 'none', border: 'none', fontSize: 13, color: 'var(--sc-text-secondary)', cursor: 'pointer', padding: 0, marginBottom: 12, display: 'inline-block' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 16px', fontFamily: "'Outfit', sans-serif" },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 14, padding: 22, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 14px', fontFamily: "'Outfit', sans-serif" },
  profileBar: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  avatar: { width: 52, height: 52, borderRadius: 14, background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18 },
  badge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, display: 'inline-block' },
  divider: { height: 1, background: 'var(--sc-divider)', margin: '16px 0' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: { padding: '5px 12px', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', borderRadius: 8, fontSize: 12, fontWeight: 600 },
  statGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  stat: { background: 'var(--sc-bg-secondary)', borderRadius: 12, padding: '16px 18px', textAlign: 'center' as const, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  statVal: { fontSize: 24, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '6px 0 2px' },
  statLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600, textTransform: 'uppercase' as const },
  btnGroup: { display: 'flex', flexDirection: 'column', gap: 10 },
  formLabel: { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, marginBottom: 6 },
  textarea: { width: '100%', minHeight: 76, border: '1px solid var(--sc-border)', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' as const, boxSizing: 'border-box' as const, background: 'var(--sc-bg-primary)', color: 'var(--sc-text-primary)', marginBottom: 12 },
  approveBtn: { padding: '12px', background: 'var(--sc-success)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14, transition: 'all 0.2s' },
  rejectBtn: { padding: '12px', background: 'var(--sc-error)', color: 'var(--sc-text-inverse)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14, transition: 'all 0.2s' },
};
